import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import useEmployeeDataStore from "../../store/useEmployeeDataStore";

const EmployeeIdEditorPopup = ({ closeModal, employee, setIsModalOpen }) => {
  const { employeeData, getEmployeeData } = useEmployeeDataStore();
  const [currentEmployee, setCurrentEmployee] = useState({
    id: employee.employee_id,
    name: employee.employee_full_name,
    email: employee.official_email,
  });

  const [digits, setDigits] = useState(["", "", "", ""]);
  const [availabilityStatus, setAvailabilityStatus] = useState("");
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const letters = currentEmployee.id.substring(0, 3);
  const currentDigits = currentEmployee.id.substring(3);
  const navigate = useNavigate();

  const employeeCompany = employee?.roles[0]?.company[0]?.company_name;

  const inputRefs = useRef([]);

  useEffect(() => {
    getEmployeeData(employeeCompany);
  }, []);

    useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // console.log("Users in EIEP", employee);
  // console.log("Employee Data in EIEP", employeeData);

  const allEmployeeIds = (employeeData ?? [])
  .flatMap((employee) => {
    const users = employee?.users;
    if (!users) return [];
    // users might be a single object, not an array
    return Array.isArray(users)
      ? users.map((user) => user?.employee_id)
      : [users?.employee_id];
  })
  .filter(Boolean);

  const checkIdAvailability = async (newId) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const idExists = allEmployeeIds.includes(newId);
    return !idExists;
  };

  const handleDigitChange = (index, value) => {
    if (value !== "" && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    setAvailabilityStatus("");
    setUpdateMessage("");

    if (value && index < 3) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    const completedDigits = newDigits.join("");
    if (completedDigits.length === 4) {
      checkAvailability(letters + completedDigits);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const checkAvailability = async (newId) => {
    if (newId === currentEmployee.id) {
      setAvailabilityStatus("current");
      return;
    }

    setIsCheckingAvailability(true);
    try {
      const isAvailable = await checkIdAvailability(newId);
      setAvailabilityStatus(isAvailable ? "available" : "exists");
    } catch (error) {
      setAvailabilityStatus("error");
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleUpdate = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const newId = letters + digits.join("");

    setIsUpdating(true);
    setUpdateMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/employees/employeeId`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            employee_information_id: employee.employee_information_id,
            emp_id: newId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.text();

      setAvailabilityStatus("");
      setUpdateMessage("User ID updated.");
      setCurrentEmployee((prev) => ({ ...prev, id: newId }));

      setTimeout(() => {
        setIsModalOpen(false);
        setUpdateMessage("");
        setAvailabilityStatus("");
        setDigits(["", "", "", ""]);

        navigate(`/employeedirectory/${employee.employee_information_id}`);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error updating employee ID:", error);
      setUpdateMessage("Failed to update the User ID.");
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusMessage = () => {
    if (isCheckingAvailability) {
      return <span className="bg-primary-hrms">Checking availability...</span>;
    }

    switch (availabilityStatus) {
      case "available":
        return <span className="text-green-600">User ID available.</span>;
      case "exists":
        return <span className="text-red-600">User ID already exists.</span>;
      case "current":
        return <span className="text-gray-600">Current User ID.</span>;
      case "error":
        return (
          <span className="text-red-600">Error checking availability.</span>
        );
      default:
        return null;
    }
  };

  const showUpdateButton =
    availabilityStatus === "available" && !isCheckingAvailability;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">
            Edit Employee ID:{" "}
            <span className="text-primary-hrms">{employee.employee_id}</span>
          </h3>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          {/* <label className="block text-sm font-medium text-gray-700 mb-3">
            Employee ID Format: Letters + 4 Digits
          </label> */}

          <div className="flex items-center gap-2 justify-center">
            <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-lg font-bold text-gray-600">
              {letters}
            </div>

            <span className="text-gray-400 text-xl">-</span>

            <div className="flex gap-1 ">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  id={`digit-${index}`}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  className="w-12 h-12 border border-gray-300 rounded text-center !text-lg font-mono font-bold focus:border-primary-hrms focus:outline-none focus:ring-2 focus:ring-green-200"
                  placeholder="_"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        <div className="mb-4 min-h-6 flex items-center justify-center">
          {getStatusMessage()}
          {updateMessage && (
            <span
              className={`${
                updateMessage.includes("updated")
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {updateMessage}
            </span>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isUpdating}
          >
            Cancel
          </button>

          {showUpdateButton && (
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className={`px-4 py-2 bg-primary-hrms text-white rounded hover:bg-green-600 transition-colors flex items-center gap-2 ${
                isUpdating ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </button>
          )}
        </div>

        <div className="mt-4 text-xs text-gray-500 text-center">
          Enter 4 digits to check availability. Only numbers are allowed.
        </div>
      </div>
    </div>
  );
};

export default EmployeeIdEditorPopup;
